import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faTrash, 
  faSpinner, 
  faCheck, 
  faClock,
  faUsers,
  faCheckCircle,
  faTimesCircle,
  faComments,
  faArrowLeft,
  faEnvelope,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { shouldFilterByAssignedClients } from '../../utils/permissions';
import './styles/MassMessaging.css';

const API_BASE_URL = 'https://track.snapped.cc';

// Add axios interceptor for authentication
axios.interceptors.request.use(async (config) => {
  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens.idToken.toString();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
});

const MassMessaging = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('compose'); // compose, queue, sent, conversations
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [messagingStatus, setMessagingStatus] = useState({ type: '', message: '' });
  const [hiddenConversations, setHiddenConversations] = useState(new Set());
  const [showHiddenConversations, setShowHiddenConversations] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Message Suggestions state
  const [unansweredMessages, setUnansweredMessages] = useState([]);
  const [selectedUnanswered, setSelectedUnanswered] = useState(null);
  const [suggestedReply, setSuggestedReply] = useState('');
  const [editedReply, setEditedReply] = useState('');
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackNote, setFeedbackNote] = useState('');
  
  // Recurring schedules state
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [recurringForm, setRecurringForm] = useState({
    name: '',
    contact_ids: [],
    message_text: '',
    frequency: 'weekly',
    time_of_day: '09:00',
    day_of_week: 1,
    day_of_month: 1,
    is_active: true
  });

  useEffect(() => {
    fetchContacts();
    fetchQueue();
    fetchSentMessages();
    
    // Refresh queue every 10 seconds
    const interval = setInterval(() => {
      if (activeTab === 'queue') {
        fetchQueue();
      } else if (activeTab === 'sent') {
        fetchSentMessages();
      } else if (activeTab === 'conversations') {
        fetchConversations();
      } else if (activeTab === 'recurring') {
        fetchRecurringSchedules();
      } else if (activeTab === 'suggestions') {
        fetchUnansweredMessages();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  // Scroll to bottom when new messages load
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/contacts`);
      console.log('Fetched contacts:', response.data);
      // Filter contacts that have group_chat_id and are enabled for messaging
      let validContacts = response.data.filter(c => 
        c.group_chat_id && c.auto_messaging_enabled !== false
      );
      console.log('Valid contacts for messaging:', validContacts.length);
      
      // Apply role-based filtering for CSM users
      if (shouldFilterByAssignedClients(user?.role)) {
        const assignedClients = user?.assignedClients || [];
        validContacts = validContacts.filter(c => 
          assignedClients.includes(c.client_id) || assignedClients.includes(c.id)
        );
        console.log('Filtered contacts by assigned clients:', validContacts.length);
      }
      
      setContacts(validContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    }
  };

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/queue`);
      setQueue(response.data.queue_items || []);
    } catch (error) {
      console.error('Error fetching queue:', error);
    }
  };

  const fetchSentMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/queue/sent?limit=50`);
      setSentMessages(response.data.sent_items || []);
    } catch (error) {
      console.error('Error fetching sent messages:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/conversations`);
      setConversations(response.data.conversations || []);
      
      // Fetch hidden conversations
      const hiddenResponse = await axios.get(`${API_BASE_URL}/api/messaging/hidden-conversations`);
      setHiddenConversations(new Set(hiddenResponse.data.hidden_thread_ids || []));
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    }
  };

  const fetchRecurringSchedules = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/recurring/list`);
      setRecurringSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Error fetching recurring schedules:', error);
      toast.error('Failed to load recurring schedules');
    }
  };

  const fetchUnansweredMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/unanswered-messages?limit=50`);
      setUnansweredMessages(response.data.unanswered_messages || []);
    } catch (error) {
      console.error('Error fetching unanswered messages:', error);
      toast.error('Failed to load unanswered messages');
    }
  };

  const fetchConversationMessages = async (threadId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/messaging/conversations/${encodeURIComponent(threadId)}/messages`);
      setConversationMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const toggleConversationVisibility = async (threadId, e) => {
    e.stopPropagation(); // Prevent conversation from being selected
    try {
      await axios.post(`${API_BASE_URL}/api/messaging/hide-conversation`, {
        thread_id: threadId
      });
      
      setHiddenConversations(prev => {
        const newSet = new Set(prev);
        newSet.add(threadId);
        return newSet;
      });
      
      toast.success('Conversation hidden');
    } catch (error) {
      console.error('Error hiding conversation:', error);
      toast.error('Failed to hide conversation');
    }
  };

  const unhideConversation = async (threadId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_BASE_URL}/api/messaging/unhide-conversation`, {
        thread_id: threadId
      });
      
      setHiddenConversations(prev => {
        const newSet = new Set(prev);
        newSet.delete(threadId);
        return newSet;
      });
      
      toast.success('Conversation unhidden');
    } catch (error) {
      console.error('Error unhiding conversation:', error);
      toast.error('Failed to unhide conversation');
    }
  };

  const handleSelectConversation = (conversation) => {
    console.log('Selected conversation platform:', conversation.platform, 'thread_id:', conversation.thread_id);
    setSelectedConversation(conversation);
    fetchConversationMessages(conversation.thread_id);
  };

  const handleBackToConversations = () => {
    setSelectedConversation(null);
    setConversationMessages([]);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) {
      setMessagingStatus({ type: 'error', message: 'Please enter a message' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
      return;
    }

    setSendingReply(true);
    
    // Create temporary message ID for tracking
    const tempMessageId = `temp-${Date.now()}`;
    
    // Immediately add pending message to UI
    const pendingMessage = {
      message_id: tempMessageId,
      text: replyText,
      timestamp: new Date().toISOString(),
      direction: 'outbound',
      is_from_client: true,
      sender: 'You',
      phone_number: selectedConversation.phone_number,
      is_group: selectedConversation.is_group,
      attachments: [],
      platform: selectedConversation.platform || 'imessage',  // Use conversation platform
      _pending: true // Flag to show spinner/low opacity
    };
    
    setConversationMessages(prev => [...prev, pendingMessage]);
    const messageToSend = replyText;
    setReplyText(''); // Clear input immediately
    
    try {
      // Create a queue entry for this message
      const response = await axios.post(`${API_BASE_URL}/api/messaging/send-to-conversation`, {
        thread_id: selectedConversation.thread_id,
        message_text: messageToSend,
        group_id: selectedConversation.is_group ? selectedConversation.thread_id : null,
        phone_number: selectedConversation.phone_number,
        contact_name: selectedConversation.contact_name,
        is_group: selectedConversation.is_group,
        platform: selectedConversation.platform || 'imessage',  // Use conversation platform
        client_id: selectedConversation.client_id
      });

      // Update the pending message with the real one from server
      setConversationMessages(prev => 
        prev.map(msg => 
          msg.message_id === tempMessageId 
            ? { ...response.data.message, _pending: false } 
            : msg
        )
      );

      setMessagingStatus({ type: 'success', message: 'Message sent successfully!' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 3000);
      
      // Still refresh after delay to catch any updates (read receipts, etc)
      setTimeout(() => {
        fetchConversationMessages(selectedConversation.thread_id);
      }, 2000);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove the pending message on error
      setConversationMessages(prev => prev.filter(msg => msg.message_id !== tempMessageId));
      
      setMessagingStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to send message' 
      });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
      
      // Restore the message text so user can retry
      setReplyText(messageToSend);
    } finally {
      setSendingReply(false);
    }
  };

  const handleSelectContact = (clientId) => {
    setSelectedContacts(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.client_id));
    }
  };

  const handleSelectGroup = (groupType) => {
    let groupContacts = [];
    
    switch(groupType) {
      case 'monetized':
        groupContacts = filteredContacts.filter(c => c.is_monetized === true);
        break;
      case 'verified':
        groupContacts = filteredContacts.filter(c => c.is_verified === true);
        break;
      case 'signed':
        groupContacts = filteredContacts.filter(c => c.is_signed === true);
        break;
      default:
        groupContacts = filteredContacts;
    }
    
    setSelectedContacts(groupContacts.map(c => c.client_id));
    toast.success(`Selected ${groupContacts.length} ${groupType} clients`);
  };

  const handleScheduleMessage = async () => {
    if (!messageText.trim()) {
      setMessagingStatus({ type: 'error', message: 'Please enter a message' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
      return;
    }
    
    if (selectedContacts.length === 0) {
      setMessagingStatus({ type: 'error', message: 'Please select at least one contact' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
      return;
    }
    
    setLoading(true);
    try {
      // Group contacts by message_location platform
      const selectedContactsData = contacts.filter(c => selectedContacts.includes(c.client_id));
      
      const imessageContacts = selectedContactsData
        .filter(c => !c.message_location || c.message_location === 'iMessage' || c.message_location === 'SMS')
        .map(c => c.client_id);
      
      const whatsappContacts = selectedContactsData
        .filter(c => c.message_location === 'WhatsApp')
        .map(c => c.client_id);
      
      const telegramContacts = selectedContactsData
        .filter(c => c.message_location === 'Telegram')
        .map(c => c.client_id);
      
      let totalScheduled = 0;
      let totalFailed = 0;
      const allFailedContacts = [];
      
      // Send to iMessage/SMS
      if (imessageContacts.length > 0) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/messaging/schedule`, {
            contact_ids: imessageContacts,
            message_text: messageText,
            scheduled_for: null // Send immediately
          });
          totalScheduled += response.data.scheduled_count || 0;
          if (response.data.failed_contacts) {
            allFailedContacts.push(...response.data.failed_contacts);
            totalFailed += response.data.failed_contacts.length;
          }
        } catch (error) {
          console.error('Error scheduling iMessage:', error);
          totalFailed += imessageContacts.length;
        }
      }
      
      // Send to WhatsApp
      if (whatsappContacts.length > 0) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/whatsapp/schedule`, {
            contact_ids: whatsappContacts,
            message_text: messageText,
            scheduled_for: null // Send immediately
          });
          totalScheduled += response.data.scheduled_count || 0;
          if (response.data.failed_contacts) {
            allFailedContacts.push(...response.data.failed_contacts);
            totalFailed += response.data.failed_contacts.length;
          }
        } catch (error) {
          console.error('Error scheduling WhatsApp:', error);
          totalFailed += whatsappContacts.length;
        }
      }
      
      // Send to Telegram (placeholder for future)
      if (telegramContacts.length > 0) {
        console.warn('Telegram messaging not yet implemented');
        totalFailed += telegramContacts.length;
      }
      
      // Show success status
      if (totalScheduled > 0) {
        const platformBreakdown = [];
        if (imessageContacts.length > 0) platformBreakdown.push(`${imessageContacts.length} iMessage`);
        if (whatsappContacts.length > 0) platformBreakdown.push(`${whatsappContacts.length} WhatsApp`);
        
        setMessagingStatus({ 
          type: 'success', 
          message: `Successfully scheduled ${totalScheduled} messages (${platformBreakdown.join(', ')})!` 
        });
        setTimeout(() => setMessagingStatus({ type: '', message: '' }), 3000);
      }
      
      if (totalFailed > 0) {
        setTimeout(() => {
          setMessagingStatus({ 
            type: 'error', 
            message: `Failed to schedule ${totalFailed} messages` 
          });
          setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
        }, 3500);
      }
      
      // Reset form
      setMessageText('');
      setSelectedContacts([]);
      
      // Refresh queue
      fetchQueue();
    } catch (error) {
      console.error('Error scheduling message:', error);
      setMessagingStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to schedule messages' 
      });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromQueue = async (messageId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/messaging/queue/${messageId}`);
      setMessagingStatus({ type: 'success', message: 'Removed from queue successfully!' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 3000);
      fetchQueue();
    } catch (error) {
      console.error('Error removing from queue:', error);
      setMessagingStatus({ type: 'error', message: 'Failed to remove from queue' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
    }
  };

  const handleClearQueue = async () => {
    if (!window.confirm('Are you sure you want to clear ALL pending messages?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/messaging/queue/clear-all`);
      setMessagingStatus({ 
        type: 'success', 
        message: `Successfully removed ${response.data.deleted_count} messages from queue!` 
      });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 3000);
      fetchQueue();
    } catch (error) {
      console.error('Error clearing queue:', error);
      setMessagingStatus({ type: 'error', message: 'Failed to clear queue' });
      setTimeout(() => setMessagingStatus({ type: '', message: '' }), 4000);
    }
  };

  // Recurring schedule handlers
  const handleCreateRecurringSchedule = async () => {
    if (!recurringForm.name.trim()) {
      toast.error('Please enter a name for this schedule');
      return;
    }
    if (!recurringForm.message_text.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (recurringForm.contact_ids.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/messaging/recurring/create`, recurringForm);
      toast.success('Recurring schedule created successfully!');
      setShowRecurringForm(false);
      setRecurringForm({
        name: '',
        contact_ids: [],
        message_text: '',
        frequency: 'weekly',
        time_of_day: '09:00',
        day_of_week: 1,
        day_of_month: 1,
        is_active: true
      });
      fetchRecurringSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error('Failed to create schedule');
    }
  };

  const handleToggleRecurringSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/messaging/recurring/${scheduleId}/toggle`);
      toast.success('Schedule updated');
      fetchRecurringSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast.error('Failed to update schedule');
    }
  };

  const handleDeleteRecurringSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this recurring schedule?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/messaging/recurring/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchRecurringSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  // Message Suggestion Handlers
  const handleSelectUnanswered = async (message) => {
    setSelectedUnanswered(message);
    setSuggestedReply('');
    setEditedReply('');
    setShowFeedbackForm(false);
    
    // Auto-generate suggestion
    setLoadingSuggestion(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/messaging/suggest-reply`, {
        thread_id: message.thread_id,
        context_messages: message.context_messages,
        client_id: message.client_id,
        performance_data: message.performance_data
      });
      
      setSuggestedReply(response.data.suggested_reply);
      setEditedReply(response.data.suggested_reply);
    } catch (error) {
      console.error('Error generating suggestion:', error);
      toast.error('Failed to generate suggestion');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSendSuggestedReply = async () => {
    if (!editedReply.trim() || !selectedUnanswered) {
      toast.error('Please enter a message');
      return;
    }

    setSendingSuggestion(true);
    try {
      await axios.post(`${API_BASE_URL}/api/messaging/send-to-conversation`, {
        thread_id: selectedUnanswered.thread_id,
        message_text: editedReply,
        phone_number: selectedUnanswered.phone_number,
        contact_name: selectedUnanswered.contact_name,
        is_group: selectedUnanswered.is_group
      });

      toast.success('Message sent! Please rate the AI suggestion.');
      
      // Refresh unanswered messages in background
      fetchUnansweredMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingSuggestion(false);
    }
  };

  const handleRegenerateSuggestion = async () => {
    if (!selectedUnanswered) return;
    
    setLoadingSuggestion(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/messaging/suggest-reply`, {
        thread_id: selectedUnanswered.thread_id,
        context_messages: selectedUnanswered.context_messages,
        client_id: selectedUnanswered.client_id,
        performance_data: selectedUnanswered.performance_data
      });
      
      setSuggestedReply(response.data.suggested_reply);
      setEditedReply(response.data.suggested_reply);
      toast.success('New suggestion generated');
    } catch (error) {
      console.error('Error generating suggestion:', error);
      toast.error('Failed to generate suggestion');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackType) {
      toast.error('Please select feedback type');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/messaging/feedback-on-reply`, {
        thread_id: selectedUnanswered.thread_id,
        original_suggestion: suggestedReply,
        edited_reply: editedReply,
        feedback_type: feedbackType,
        feedback_note: feedbackNote
      });

      toast.success('Feedback submitted! Thanks for helping improve suggestions.');
      
      // Reset everything and go back to list
      setShowFeedbackForm(false);
      setFeedbackType('');
      setFeedbackNote('');
      setSelectedUnanswered(null);
      setSuggestedReply('');
      setEditedReply('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  const handleSkipFeedback = () => {
    // Skip feedback and go back to list
    setShowFeedbackForm(false);
    setFeedbackType('');
    setFeedbackNote('');
    setSelectedUnanswered(null);
    setSuggestedReply('');
    setEditedReply('');
  };

  const filteredContacts = contacts.filter(contact => 
    contact.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone_number?.includes(searchTerm)
  );

  return (
    <div className="massmessaging-container">
      <div className="massmessaging-header">
        <h1>Mass Messaging Scheduler</h1>
        <p>Schedule messages to be sent to multiple clients via iMessage</p>
      </div>

      {/* Messaging Status Message */}
      {messagingStatus.message && (
        <div className={`massmessaging-status-message massmessaging-status-${messagingStatus.type}`}>
          {messagingStatus.message}
        </div>
      )}

      <div className="massmessaging-tabs">
        <button 
          className={`massmessaging-tab-button ${activeTab === 'compose' ? 'active' : ''}`}
          onClick={() => setActiveTab('compose')}
        >
          <FontAwesomeIcon icon={faPaperPlane} /> Compose
        </button>
        <button 
          className={`massmessaging-tab-button ${activeTab === 'suggestions' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('suggestions');
            fetchUnansweredMessages();
          }}
        >
          <FontAwesomeIcon icon={faComments} /> Suggestions ({unansweredMessages.length})
        </button>
        <button 
          className={`massmessaging-tab-button ${activeTab === 'conversations' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('conversations');
            fetchConversations();
          }}
        >
          <FontAwesomeIcon icon={faComments} /> Conversations
        </button>
        <button 
          className={`massmessaging-tab-button ${activeTab === 'recurring' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('recurring');
            fetchRecurringSchedules();
          }}
        >
          <FontAwesomeIcon icon={faClock} /> Recurring
        </button>
        <button 
          className={`massmessaging-tab-button ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          <FontAwesomeIcon icon={faClock} /> Queue ({queue.length})
        </button>
        <button 
          className={`massmessaging-tab-button ${activeTab === 'sent' ? 'active' : ''}`}
          onClick={() => setActiveTab('sent')}
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Sent
        </button>
      </div>

      {activeTab === 'compose' && (
        <div className="massmessaging-compose-section">
          {/* Quick Groups Section */}
          <div className="massmessaging-quick-groups">
            <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Quick Groups</h3>
            <div className="massmessaging-quick-groups-buttons">
              <button 
                className="massmessaging-btn-group"
                onClick={() => handleSelectGroup('monetized')}
                title="Select all monetized clients"
              >
                <FontAwesomeIcon icon={faCheckCircle} /> All Monetized
                <span className="massmessaging-group-count">
                  ({filteredContacts.filter(c => c.is_monetized === true).length})
                </span>
              </button>
              <button 
                className="massmessaging-btn-group"
                onClick={() => handleSelectGroup('verified')}
                title="Select all verified clients"
              >
                <FontAwesomeIcon icon={faCheck} /> All Verified
                <span className="massmessaging-group-count">
                  ({filteredContacts.filter(c => c.is_verified === true).length})
                </span>
              </button>
              <button 
                className="massmessaging-btn-group"
                onClick={() => handleSelectGroup('signed')}
                title="Select all signed clients"
              >
                <FontAwesomeIcon icon={faUsers} /> All Signed
                <span className="massmessaging-group-count">
                  ({filteredContacts.filter(c => c.is_signed === true).length})
                </span>
              </button>
            </div>
          </div>

          <div className="massmessaging-compose-grid">
            {/* Contact Selection */}
            <div className="massmessaging-contacts-panel">
              <div className="massmessaging-panel-header">
                <h3>Select Recipients ({selectedContacts.length}/{filteredContacts.length})</h3>
                <button className="massmessaging-btn-select-all" onClick={handleSelectAll}>
                  {selectedContacts.length === filteredContacts.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="massmessaging-search-box">
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="massmessaging-contacts-list">
                {filteredContacts.length === 0 ? (
                  <div className="massmessaging-empty-state">
                    <FontAwesomeIcon icon={faUsers} size="3x" style={{ marginBottom: '16px', color: '#ddd' }} />
                    <p>No contacts available for messaging</p>
                    <small>Contacts need:</small>
                    <ul style={{ textAlign: 'left', marginTop: '8px', color: '#999' }}>
                      <li>A synced group chat ID</li>
                      <li>Auto-messaging enabled</li>
                    </ul>
                    <small style={{ marginTop: '12px', display: 'block' }}>
                      Go to CLIENTS → Edit → Messaging section to sync contacts
                    </small>
                  </div>
                ) : (
                  filteredContacts.map((contact) => {
                    const isAgency = contact.is_agency_contact;
                    return (
                      <div 
                        key={contact.client_id} 
                        className={`massmessaging-contact-item ${selectedContacts.includes(contact.client_id) ? 'selected' : ''}`}
                        onClick={() => handleSelectContact(contact.client_id)}
                        style={{
                          backgroundColor: isAgency ? '#fff3cd' : 'white',
                          border: isAgency ? '2px solid #ffc107' : '1px solid #e0e0e0'
                        }}
                      >
                        <div className="massmessaging-contact-checkbox">
                          <input 
                            type="checkbox" 
                            checked={selectedContacts.includes(contact.client_id)}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="massmessaging-contact-info">
                          <div className="massmessaging-contact-name">
                            {contact.client_name || 'Unknown'}
                            {isAgency && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '11px',
                                padding: '2px 6px',
                                backgroundColor: '#856404',
                                color: 'white',
                                borderRadius: '10px',
                                fontWeight: '500'
                              }}>
                                AGENCY
                              </span>
                            )}
                          </div>
                          <div className="massmessaging-contact-phone">
                            {contact.phone_number}
                            {isAgency && contact.client_count && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '11px',
                                color: '#856404',
                                fontWeight: '500'
                              }}>
                                • Represents {contact.client_count} client{contact.client_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {isAgency && contact.agency_contact_person && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                              Contact: {contact.agency_contact_person}
                            </div>
                          )}
                          <span className={`massmessaging-platform-badge ${contact.message_location?.toLowerCase() || 'imessage'}`}>
                            {contact.message_location || 'iMessage'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Message Composition */}
            <div className="massmessaging-message-panel">
              <div className="massmessaging-panel-header">
                <h3>Message</h3>
              </div>
              
              <textarea
                className="massmessaging-message-textarea"
                placeholder="Type your message here..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={10}
              />
              
              <div className="massmessaging-message-stats">
                <span>{messageText.length} characters</span>
                <span>
                  {selectedContacts.length} recipient{selectedContacts.length !== 1 ? 's' : ''}
                  {(() => {
                    // Calculate how many agency contacts and individual clients
                    const selectedContactsData = contacts.filter(c => selectedContacts.includes(c.client_id));
                    const agencyContacts = selectedContactsData.filter(c => c.is_agency_contact);
                    const individualContacts = selectedContactsData.filter(c => !c.is_agency_contact);
                    const totalClients = individualContacts.length + agencyContacts.reduce((sum, a) => sum + (a.client_count || 0), 0);
                    
                    if (agencyContacts.length > 0 && totalClients !== selectedContacts.length) {
                      return ` (${totalClients} total clients via ${agencyContacts.length} ${agencyContacts.length === 1 ? 'agency' : 'agencies'})`;
                    }
                    return '';
                  })()}
                </span>
              </div>

              <button 
                className="massmessaging-btn-send"
                onClick={handleScheduleMessage}
                disabled={loading || !messageText.trim() || selectedContacts.length === 0}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Scheduling...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} /> Schedule Messages
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="massmessaging-queue-section">
          <div className="massmessaging-queue-header">
            <h3>Pending Messages ({queue.length})</h3>
            {queue.length > 0 && (
              <button className="massmessaging-btn-clear-queue" onClick={handleClearQueue}>
                <FontAwesomeIcon icon={faTrash} /> Clear All
              </button>
            )}
          </div>

          {queue.length === 0 ? (
            <div className="massmessaging-empty-state">
              <FontAwesomeIcon icon={faClock} size="3x" />
              <p>No pending messages in queue</p>
            </div>
          ) : (
            <div className="massmessaging-queue-list">
              {queue.map((item) => (
                <div key={item.message_id} className="massmessaging-queue-item">
                  <div className="massmessaging-queue-item-header">
                    <div>
                      <span className="massmessaging-queue-recipient">{item.client_name || item.group_name || 'Unknown'}</span>
                      {item.platform && (
                        <span className={`massmessaging-platform-badge ${item.platform.toLowerCase()}`} style={{ marginLeft: '8px' }}>
                          {item.platform}
                        </span>
                      )}
                    </div>
                    <button 
                      className="massmessaging-btn-remove"
                      onClick={() => handleRemoveFromQueue(item.message_id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                  <div className="massmessaging-queue-item-message">{item.message_text}</div>
                  <div className="massmessaging-queue-item-meta">
                    <span>Created: {new Date(item.created_at).toLocaleString()}</span>
                    {item.scheduled_by && <span>By: {item.scheduled_by}</span>}
                    {item.status && <span>Status: {item.status}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'sent' && (
        <div className="massmessaging-sent-section">
          <div className="massmessaging-sent-header">
            <h3>Sent Messages ({sentMessages.length})</h3>
          </div>

          {sentMessages.length === 0 ? (
            <div className="massmessaging-empty-state">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" />
              <p>No sent messages yet</p>
            </div>
          ) : (
            <div className="massmessaging-sent-list">
              {sentMessages.map((item) => (
                <div key={item.message_id} className="massmessaging-sent-item">
                  <div className="massmessaging-sent-item-header">
                    <div>
                      <span className="massmessaging-sent-recipient">{item.client_name || item.group_name || 'Unknown'}</span>
                      {item.platform && (
                        <span className={`massmessaging-platform-badge ${item.platform.toLowerCase()}`} style={{ marginLeft: '8px' }}>
                          {item.platform}
                        </span>
                      )}
                    </div>
                    <span className={`massmessaging-status-badge ${item.error ? 'massmessaging-badge-error' : 'massmessaging-badge-success'}`}>
                      <FontAwesomeIcon icon={item.error ? faTimesCircle : faCheckCircle} />
                      {item.error ? 'Failed' : 'Sent'}
                    </span>
                  </div>
                  <div className="massmessaging-sent-item-message">{item.message_text}</div>
                  <div className="massmessaging-sent-item-meta">
                    {item.sent_at && <span>Sent: {new Date(item.sent_at).toLocaleString()}</span>}
                    {item.error && <span className="massmessaging-error-text">Error: {item.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'suggestions' && (
        <div className="massmessaging-suggestions-section">
          {!selectedUnanswered ? (
            <>
              <div className="massmessaging-suggestions-header">
                <h3>Unanswered Messages ({unansweredMessages.length})</h3>
                <button 
                  className="massmessaging-btn-refresh"
                  onClick={fetchUnansweredMessages}
                >
                  Refresh
                </button>
              </div>

              {unansweredMessages.length === 0 ? (
                <div className="massmessaging-empty-state">
                  <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ color: '#28a745' }} />
                  <p>All caught up! No unanswered messages.</p>
                  <small>Great job staying on top of your conversations!</small>
                </div>
              ) : (
                <div className="massmessaging-unanswered-list">
                  {unansweredMessages.map((msg) => (
                    <div 
                      key={msg.thread_id} 
                      className="massmessaging-unanswered-item"
                      onClick={() => handleSelectUnanswered(msg)}
                    >
                      <div className="massmessaging-unanswered-header">
                        <div className="massmessaging-unanswered-avatar">
                          <FontAwesomeIcon icon={faUsers} />
                        </div>
                        <div className="massmessaging-unanswered-info">
                          <div className="massmessaging-unanswered-name">
                            {msg.contact_name || msg.phone_number}
                          </div>
                          <div className="massmessaging-unanswered-time">
                            {msg.last_message.timestamp ? new Date(msg.last_message.timestamp).toLocaleString() : ''}
                          </div>
                        </div>
                        {msg.performance_data && (
                          <div className="massmessaging-performance-badge">
                            <span>📊 {msg.performance_data.views ? `${(msg.performance_data.views / 1000).toFixed(1)}k views` : 'No data'}</span>
                          </div>
                        )}
                      </div>
                      <div className="massmessaging-unanswered-message">
                        {msg.last_message.text}
                      </div>
                      <div className="massmessaging-unanswered-context">
                        {msg.context_messages.length} messages in conversation
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="massmessaging-suggestion-view">
              <div className="massmessaging-suggestion-header">
                <button 
                  className="massmessaging-back-button" 
                  onClick={() => {
                    setSelectedUnanswered(null);
                    setSuggestedReply('');
                    setEditedReply('');
                    setShowFeedbackForm(false);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to List
                </button>
                <div className="massmessaging-suggestion-header-info">
                  <h3>{selectedUnanswered.contact_name || selectedUnanswered.phone_number}</h3>
                  {selectedUnanswered.performance_data && (
                    <span className="massmessaging-performance-info">
                      {selectedUnanswered.performance_data.views && 
                        `📊 ${(selectedUnanswered.performance_data.views / 1000).toFixed(1)}k views`}
                    </span>
                  )}
                </div>
              </div>

              {/* Conversation Context */}
              <div className="massmessaging-suggestion-context">
                <h4>Recent Conversation</h4>
                <div className="massmessaging-context-messages">
                  {selectedUnanswered.context_messages.map((msg, idx) => (
                    <div 
                      key={idx}
                      className={`massmessaging-context-message ${msg.direction === 'outbound' ? 'outbound' : 'inbound'}`}
                    >
                      <div className="massmessaging-context-message-sender">
                        {msg.direction === 'outbound' ? 'You' : (msg.sender || 'Client')}
                      </div>
                      <div className="massmessaging-context-message-text">{msg.text}</div>
                      <div className="massmessaging-context-message-time">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Suggestion */}
              <div className="massmessaging-suggestion-box">
                <div className="massmessaging-suggestion-box-header">
                  <h4>
                    <FontAwesomeIcon icon={faSpinner} spin={loadingSuggestion} /> 
                    {loadingSuggestion ? ' Generating AI Suggestion...' : ' AI Suggested Reply'}
                  </h4>
                  {!loadingSuggestion && suggestedReply && (
                    <button 
                      className="massmessaging-btn-regenerate"
                      onClick={handleRegenerateSuggestion}
                      disabled={loadingSuggestion}
                    >
                      🔄 Regenerate
                    </button>
                  )}
                </div>

                {suggestedReply && (
                  <>
                    <textarea
                      className="massmessaging-suggestion-textarea"
                      value={editedReply}
                      onChange={(e) => setEditedReply(e.target.value)}
                      rows={6}
                      placeholder="AI suggestion will appear here..."
                    />
                    
                    {/* Feedback Form - Smart Questions */}
                    <div className="STYLE-message-ai-feedback-container">
                      <div className="STYLE-message-ai-feedback-primary">
                        <h4>Rate this AI suggestion:</h4>
                        <div className="STYLE-message-ai-feedback-primary-buttons">
                          <button 
                            className={`STYLE-message-ai-feedback-primary-btn good ${feedbackType === 'good' ? 'active' : ''}`}
                            onClick={() => {
                              setFeedbackType('good');
                              setShowFeedbackForm(true);
                            }}
                          >
                            <span className="feedback-icon">✓</span>
                            <span className="feedback-label">Good to send</span>
                          </button>
                          <button 
                            className={`STYLE-message-ai-feedback-primary-btn needs-work ${feedbackType === 'needs_feedback' ? 'active' : ''}`}
                            onClick={() => {
                              setFeedbackType('needs_feedback');
                              setShowFeedbackForm(true);
                            }}
                          >
                            <span className="feedback-icon">!</span>
                            <span className="feedback-label">Needs improvement</span>
                          </button>
                        </div>
                      </div>

                      {/* Detailed Questions Based on Choice */}
                      {showFeedbackForm && feedbackType === 'good' && (
                        <div className="STYLE-message-ai-feedback-details">
                          <h5>What made this suggestion good?</h5>
                          <div className="STYLE-message-ai-feedback-checkboxes">
                            <label>
                              <input type="checkbox" value="tone" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Perfect tone & friendliness</span>
                            </label>
                            <label>
                              <input type="checkbox" value="specific" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Specific & actionable</span>
                            </label>
                            <label>
                              <input type="checkbox" value="context" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Understood the context well</span>
                            </label>
                            <label>
                              <input type="checkbox" value="length" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Right length</span>
                            </label>
                            <label>
                              <input type="checkbox" value="professional" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Professional yet personal</span>
                            </label>
                          </div>
                          <textarea
                            className="STYLE-message-ai-feedback-note-small"
                            placeholder="Anything else? (optional)"
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            rows={2}
                          />
                        </div>
                      )}

                      {showFeedbackForm && feedbackType === 'needs_feedback' && (
                        <div className="STYLE-message-ai-feedback-details">
                          <h5>What needs improvement?</h5>
                          <div className="STYLE-message-ai-feedback-checkboxes">
                            <label>
                              <input type="checkbox" value="too_formal" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Too formal/stiff</span>
                            </label>
                            <label>
                              <input type="checkbox" value="too_casual" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Too casual</span>
                            </label>
                            <label>
                              <input type="checkbox" value="missed_context" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Missed key context</span>
                            </label>
                            <label>
                              <input type="checkbox" value="too_long" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Too long/wordy</span>
                            </label>
                            <label>
                              <input type="checkbox" value="too_short" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Too short/vague</span>
                            </label>
                            <label>
                              <input type="checkbox" value="wrong_tone" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Wrong tone for this client</span>
                            </label>
                            <label>
                              <input type="checkbox" value="not_actionable" onChange={(e) => {
                                const val = e.target.value;
                                setFeedbackNote(prev => e.target.checked ? `${prev} ${val}`.trim() : prev.replace(val, '').trim());
                              }} />
                              <span>Not actionable enough</span>
                            </label>
                          </div>
                          <textarea
                            className="STYLE-message-ai-feedback-note-small"
                            placeholder="What should it have said instead? (helps AI learn)"
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            rows={3}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="massmessaging-suggestion-actions">
                      <button 
                        className="massmessaging-btn-send-suggestion"
                        onClick={handleSendSuggestedReply}
                        disabled={sendingSuggestion || !editedReply.trim()}
                      >
                        {sendingSuggestion ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} /> Send Reply
                          </>
                        )}
                      </button>
                      
                      {suggestedReply !== editedReply && (
                        <span className="massmessaging-edit-indicator">
                          ✏️ You edited this (we'll learn from your changes)
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'conversations' && (
        <div className="massmessaging-conversations-section">
          {!selectedConversation ? (
            <>
              <div className="massmessaging-conversations-header">
                <h3>Conversations ({conversations.length})</h3>
                {hiddenConversations.size > 0 && (
                  <button 
                    className="massmessaging-btn-show-hidden"
                    onClick={() => setShowHiddenConversations(!showHiddenConversations)}
                  >
                    <FontAwesomeIcon icon={showHiddenConversations ? faEye : faEyeSlash} />
                    {showHiddenConversations ? ` Hide ${hiddenConversations.size}` : ` Show ${hiddenConversations.size} Hidden`}
                  </button>
                )}
              </div>

              {conversations.length === 0 ? (
                <div className="massmessaging-empty-state">
                  <FontAwesomeIcon icon={faComments} size="3x" />
                  <p>No conversations found</p>
                  <small>Conversations will appear here once messages are synced from iMessage</small>
                </div>
              ) : (
                <div className="massmessaging-conversations-list">
                  {conversations.filter(conv => {
                    const isHidden = hiddenConversations.has(conv.thread_id);
                    return showHiddenConversations ? isHidden : !isHidden;
                  }).map((conv) => {
                    const isHidden = hiddenConversations.has(conv.thread_id);
                    return (
                      <div 
                        key={conv.thread_id} 
                        className={`massmessaging-conversation-item ${isHidden ? 'hidden-item' : ''}`}
                        onClick={() => handleSelectConversation(conv)}
                      >
                      <div className={`massmessaging-conversation-avatar ${
                        conv.platform === 'email' ? 'email' :
                        conv.platform === 'whatsapp' ? 'whatsapp' :
                        conv.platform === 'imessage' ? 'imessage' :
                        conv.is_group ? 'group' : ''
                      }`}>
                        {conv.platform === 'email' ? (
                          <FontAwesomeIcon icon={faEnvelope} />
                        ) : conv.platform === 'whatsapp' ? (
                          <FontAwesomeIcon icon={faWhatsapp} />
                        ) : conv.platform === 'imessage' ? (
                          <FontAwesomeIcon icon={faApple} />
                        ) : conv.is_group ? (
                          <FontAwesomeIcon icon={faUsers} />
                        ) : (
                          <FontAwesomeIcon icon={faCheckCircle} />
                        )}
                      </div>
                      <div className="massmessaging-conversation-info">
                        <div className="massmessaging-conversation-header-line">
                          <span className="massmessaging-conversation-name">
                            {conv.contact_name || conv.phone_number}
                          </span>
                          <span className="massmessaging-conversation-time">
                            {conv.last_timestamp ? new Date(conv.last_timestamp).toLocaleString() : ''}
                          </span>
                        </div>
                        <div className="massmessaging-conversation-preview">
                          {conv.last_message || 'No messages'}
                        </div>
                        <div className="massmessaging-conversation-meta">
                          <span>{conv.message_count} messages</span>
                          {conv.is_group && <span>{conv.participant_count} participants</span>}
                        </div>
                      </div>
                      <button 
                        className="massmessaging-hide-conversation-btn"
                        onClick={(e) => isHidden ? unhideConversation(conv.thread_id, e) : toggleConversationVisibility(conv.thread_id, e)}
                        title={isHidden ? "Unhide conversation" : "Hide conversation"}
                      >
                        <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} />
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="massmessaging-chat-view">
              <div className="massmessaging-chat-header">
                <button className="massmessaging-back-button" onClick={handleBackToConversations}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
                <div className="massmessaging-chat-header-info">
                  <h3>{selectedConversation.contact_name || selectedConversation.phone_number}</h3>
                  {selectedConversation.is_group && (
                    <span className="massmessaging-chat-participants">
                      {selectedConversation.participant_count} participants
                    </span>
                  )}
                </div>
              </div>

              <div className="massmessaging-chat-messages">
                {conversationMessages.length === 0 ? (
                  <div className="massmessaging-empty-state">
                    <p>No messages in this conversation</p>
                  </div>
                ) : (
                  <>
                    {conversationMessages.map((msg, index) => {
                      const isYou = msg.sender === 'You';
                      const isClientMessage = msg.is_from_client && !isYou; // Client sent this, but not "You"
                      
                      // Determine message class
                      let messageClass = 'massmessaging-chat-message';
                      if (isYou) {
                        messageClass += ' outbound'; // Right side
                        // Check the MESSAGE platform, not conversation platform
                        if (msg.platform === 'whatsapp') {
                          messageClass += ' whatsapp'; // Add WhatsApp styling
                        } else if (msg.platform === 'email') {
                          messageClass += ' email'; // Add Email styling
                        }
                      } else if (isClientMessage) {
                        messageClass += ' client-message'; // Light blue for client - left side
                      } else {
                        messageClass += ' inbound'; // Gray for others - left side
                      }
                      
                      // Show sender name for all left-side messages (clients and others)
                      const showSender = !isYou;
                      
                      return (
                        <div 
                          key={msg.message_id || index}
                          className={messageClass}
                          style={msg._pending ? { opacity: 0.5 } : {}}
                        >
                          {showSender && (
                            <div className="massmessaging-chat-message-sender">
                              {msg.sender || 'Unknown'}
                            </div>
                          )}
                          <div className="massmessaging-chat-message-bubble">
                            <div className="massmessaging-chat-message-text">
                              {msg.text}
                              {msg._pending && (
                                <FontAwesomeIcon 
                                  icon={faSpinner} 
                                  spin 
                                  style={{ marginLeft: '8px', fontSize: '12px' }}
                                />
                              )}
                            </div>
                            <div className="massmessaging-chat-message-time">
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <div className="massmessaging-chat-input-container">
                <textarea
                  className="massmessaging-chat-input"
                  placeholder="Type your message..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                  rows={2}
                />
                <button 
                  className="massmessaging-chat-send-button"
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                >
                  {sendingReply ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faPaperPlane} />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'recurring' && (
        <div className="massmessaging-recurring-section">
          <div className="massmessaging-recurring-header">
            <h3>Recurring Message Schedules ({recurringSchedules.length})</h3>
            <button 
              className="massmessaging-btn-create-recurring" 
              onClick={() => setShowRecurringForm(!showRecurringForm)}
            >
              {showRecurringForm ? 'Cancel' : '+ Create New Schedule'}
            </button>
          </div>

          {showRecurringForm && (
            <div className="massmessaging-recurring-form">
              <h4>Create Recurring Schedule</h4>
              
              <div className="massmessaging-form-group">
                <label>Schedule Name *</label>
                <input 
                  type="text"
                  placeholder="e.g., Weekly Monday Check-in"
                  value={recurringForm.name}
                  onChange={(e) => setRecurringForm({...recurringForm, name: e.target.value})}
                />
              </div>

              <div className="massmessaging-form-group">
                <label>Message *</label>
                <textarea
                  placeholder="Enter your message..."
                  value={recurringForm.message_text}
                  onChange={(e) => setRecurringForm({...recurringForm, message_text: e.target.value})}
                  rows={4}
                />
              </div>

              <div className="massmessaging-form-row">
                <div className="massmessaging-form-group">
                  <label>Frequency *</label>
                  <select 
                    value={recurringForm.frequency}
                    onChange={(e) => setRecurringForm({...recurringForm, frequency: e.target.value})}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="massmessaging-form-group">
                  <label>Time of Day</label>
                  <input 
                    type="time"
                    value={recurringForm.time_of_day}
                    onChange={(e) => setRecurringForm({...recurringForm, time_of_day: e.target.value})}
                  />
                </div>

                {(recurringForm.frequency === 'weekly' || recurringForm.frequency === 'biweekly') && (
                  <div className="massmessaging-form-group">
                    <label>Day of Week</label>
                    <select 
                      value={recurringForm.day_of_week}
                      onChange={(e) => setRecurringForm({...recurringForm, day_of_week: parseInt(e.target.value)})}
                    >
                      <option value="0">Monday</option>
                      <option value="1">Tuesday</option>
                      <option value="2">Wednesday</option>
                      <option value="3">Thursday</option>
                      <option value="4">Friday</option>
                      <option value="5">Saturday</option>
                      <option value="6">Sunday</option>
                    </select>
                  </div>
                )}

                {recurringForm.frequency === 'monthly' && (
                  <div className="massmessaging-form-group">
                    <label>Day of Month</label>
                    <input 
                      type="number"
                      min="1"
                      max="28"
                      value={recurringForm.day_of_month}
                      onChange={(e) => setRecurringForm({...recurringForm, day_of_month: parseInt(e.target.value)})}
                    />
                  </div>
                )}
              </div>

              <div className="massmessaging-form-group">
                <label>Select Recipients ({recurringForm.contact_ids.length} selected)</label>
                <div className="massmessaging-quick-groups-buttons">
                  <button 
                    className="massmessaging-btn-group-small"
                    onClick={() => setRecurringForm({...recurringForm, contact_ids: filteredContacts.filter(c => c.is_monetized === true).map(c => c.client_id)})}
                  >
                    All Monetized ({filteredContacts.filter(c => c.is_monetized === true).length})
                  </button>
                  <button 
                    className="massmessaging-btn-group-small"
                    onClick={() => setRecurringForm({...recurringForm, contact_ids: filteredContacts.filter(c => c.is_verified === true).map(c => c.client_id)})}
                  >
                    All Verified ({filteredContacts.filter(c => c.is_verified === true).length})
                  </button>
                  <button 
                    className="massmessaging-btn-group-small"
                    onClick={() => setRecurringForm({...recurringForm, contact_ids: filteredContacts.filter(c => c.is_signed === true).map(c => c.client_id)})}
                  >
                    All Signed ({filteredContacts.filter(c => c.is_signed === true).length})
                  </button>
                </div>
              </div>

              <button 
                className="massmessaging-btn-save-recurring"
                onClick={handleCreateRecurringSchedule}
              >
                Create Schedule
              </button>
            </div>
          )}

          {recurringSchedules.length === 0 ? (
            <div className="massmessaging-empty-state">
              <FontAwesomeIcon icon={faClock} size="3x" />
              <p>No recurring schedules yet</p>
              <small>Create a schedule to automatically send messages to groups on a regular basis</small>
            </div>
          ) : (
            <div className="massmessaging-recurring-list">
              {recurringSchedules.map((schedule) => (
                <div key={schedule.schedule_id} className={`massmessaging-recurring-item ${!schedule.is_active ? 'inactive' : ''}`}>
                  <div className="massmessaging-recurring-item-header">
                    <div>
                      <h4>{schedule.name}</h4>
                      <span className={`massmessaging-frequency-badge ${schedule.frequency}`}>
                        {schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)}
                      </span>
                      {!schedule.is_active && <span className="massmessaging-inactive-badge">Inactive</span>}
                    </div>
                    <div className="massmessaging-recurring-actions">
                      <button 
                        className={`massmessaging-btn-toggle ${schedule.is_active ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleRecurringSchedule(schedule.schedule_id)}
                        title={schedule.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button 
                        className="massmessaging-btn-delete-recurring"
                        onClick={() => handleDeleteRecurringSchedule(schedule.schedule_id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="massmessaging-recurring-message">{schedule.message_text}</div>
                  
                  <div className="massmessaging-recurring-details">
                    <span><strong>Time:</strong> {schedule.time_of_day}</span>
                    {schedule.day_of_week !== null && schedule.day_of_week !== undefined && (
                      <span><strong>Day:</strong> {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][schedule.day_of_week]}</span>
                    )}
                    {schedule.day_of_month && (
                      <span><strong>Day of Month:</strong> {schedule.day_of_month}</span>
                    )}
                    <span><strong>Recipients:</strong> {schedule.contact_ids?.length || 0}</span>
                    {schedule.last_run && (
                      <span><strong>Last Run:</strong> {new Date(schedule.last_run).toLocaleString()}</span>
                    )}
                    <span><strong>Total Runs:</strong> {schedule.total_runs || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MassMessaging;

